import axiosInstance from "@/services/axiosInstance";
import type { AxiosPromise } from "axios";
import type {
    CompanyAddress,
    EmailNotifications,
    MerchantOfRecordNote,
    OnboardingSupplier,
    PaginatedDashboardInquiriesList,
    PaginatedDashboardOrdersList,
    SupplierCompany,
    SupplierContact,
    SupplierDashboardCount,
    SupplierInquiryResponse,
    SupplierPayoutDetails,
} from "@/types/api/data-contracts";

class SupplierService {
    // eslint-disable-next-line no-use-before-define
    private static instance: SupplierService;

    public static getInstance(): SupplierService {
        if (!SupplierService.instance) {
            SupplierService.instance = new SupplierService();
        }
        return SupplierService.instance;
    }

    onBoard(payload: object) {
        return axiosInstance.post("companies/onboarding/supplier/", payload);
    }

    getSupplierById(companyId: string): AxiosPromise<SupplierCompany> {
        return axiosInstance.get(`companies/suppliers/${companyId}/`);
    }

    updateInfo(payload: object, companyId: string) {
        return axiosInstance.put(`companies/suppliers/${companyId}/`, payload);
    }

    getDocuments(companyId: string) {
        return axiosInstance.get(`companies/suppliers/${companyId}/documents/`);
    }

    uploadDocument(companyId: string, formData: FormData) {
        return axiosInstance.post(
            `companies/suppliers/${companyId}/documents/`,
            formData,
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            }
        );
    }

    deleteDocument(companyId: string, documentId: string) {
        return axiosInstance.delete(
            `companies/suppliers/${companyId}/documents/${documentId}/`
        );
    }

    getInquiries(
        filter?: {
            // eslint-disable-next-line camelcase
            supplier_state: string | null;
            name?: string | null;
            category?: string | null;
            page?: number | undefined;
        },
        ordering?: string | null
    ) {
        return axiosInstance.get("inquiries/supplier/", {
            params: {
                ...filter,
                ordering,
            },
        });
    }

    getInquiryDetails(
        inquiryId: string
    ): AxiosPromise<SupplierInquiryResponse> {
        return axiosInstance.get(`inquiries/supplier/${inquiryId}/`);
    }

    /**
     * Endpoint to retrieve requests list for Supplier's dashboard.
     * @param page
     */
    getDashboardInquiries(
        page = 0
    ): AxiosPromise<PaginatedDashboardInquiriesList> {
        return axiosInstance.get(`/inquiries/supplier/dashboard-inquiries/`, {
            params: {
                page,
            },
        });
    }

    getDashboardOrders(
        page: number
    ): AxiosPromise<PaginatedDashboardOrdersList> {
        return axiosInstance.get(`/orders/supplier/dashboard-orders/`, {
            params: { page },
        });
    }

    getEmailPreferences() {
        const url = `/notifications/emails/supplier/`;
        return axiosInstance.get(url);
    }

    updateEmailGroupsPreference(
        type: string,
        value: boolean
    ): AxiosPromise<EmailNotifications> {
        const url = `/notifications/emails/supplier/on-off-unsubscribe-groups/`;
        return axiosInstance.put(url, {
            type,
            is_activated_for_user: value,
        });
    }

    unSubscribeEmails(value: boolean) {
        const url = "/notifications/emails/supplier/all/";
        return axiosInstance.post(url, {
            all_emails: value,
        });
    }

    updateQuestionnaire(payload: object, supplierId: string) {
        const url = `/companies/suppliers/${supplierId}/questionnaire/`;
        return axiosInstance.put(url, payload);
    }

    checkQuestionnaireStatus() {
        const url = "/companies/suppliers/questionnaire_submitted/";
        return axiosInstance.get(url);
    }

    addCompanyAddress(payload: object, supplierId: string) {
        const url = `/companies/suppliers/${supplierId}/`;
        return axiosInstance.patch(url, payload);
    }

    declineInquiry(inquiryId: string, payload: object) {
        const url = `/inquiries/supplier/${inquiryId}/decline/`;
        return axiosInstance.post(url, payload);
    }

    buyerSummary(uuid: string) {
        return axiosInstance.post(`/companies/suppliers/buyer-summary/`, {
            uuid,
        });
    }

    getSupplierStats(): AxiosPromise<SupplierDashboardCount> {
        return axiosInstance.get("/companies/suppliers/dashboard/count/");
    }

    getSupplierContacts(supplierUuid: string): AxiosPromise<SupplierContact[]> {
        return axiosInstance.get(
            `/companies/merchant-of-record-suppliers/${supplierUuid}/contacts/`
        );
    }

    createSupplierContact(
        payload: Object,
        supplierUuid: string
    ): AxiosPromise<SupplierContact> {
        return axiosInstance.post(
            `/companies/merchant-of-record-suppliers/${supplierUuid}/contacts/`,
            payload
        );
    }

    updateSupplierContact(
        payload: Object,
        supplierUuid: string,
        contactUuid: string
    ): AxiosPromise<SupplierContact> {
        return axiosInstance.put(
            `/companies/merchant-of-record-suppliers/${supplierUuid}/contacts/${contactUuid}/`,
            payload
        );
    }

    deleteSupplierContact(
        supplierUuid: string,
        contactUuid: string
    ): AxiosPromise {
        return axiosInstance.delete(
            `/companies/merchant-of-record-suppliers/${supplierUuid}/contacts/${contactUuid}/`
        );
    }

    getSupplierPayoutDetails(
        supplierUuid: string
    ): AxiosPromise<SupplierPayoutDetails> {
        return axiosInstance.get(
            `/companies/merchant-of-record-suppliers/${supplierUuid}/payout-details/`
        );
    }

    createSupplierPayoutDetails(
        payload: Object,
        supplierUuid: string
    ): AxiosPromise<SupplierPayoutDetails> {
        return axiosInstance.post(
            `/companies/merchant-of-record-suppliers/${supplierUuid}/payout-details/`,
            payload
        );
    }

    createOnboardingSupplierPayoutDetails(
        payload: Object,
        onboardingSupplierUuid: string
    ): AxiosPromise<SupplierPayoutDetails> {
        return axiosInstance.post(
            `/companies/mor/onboarding-suppliers/${onboardingSupplierUuid}/payout-details/`,
            payload
        );
    }

    getOnboardingSupplierPayoutDetails(
        onboardingSupplierUuid: string
    ): AxiosPromise<SupplierPayoutDetails> {
        return axiosInstance.get(
            `/companies/mor/onboarding-suppliers/${onboardingSupplierUuid}/payout-details/`
        );
    }

    updateSupplierPayoutDetails(
        payload: Object,
        supplierUuid: string
    ): AxiosPromise<SupplierPayoutDetails> {
        return axiosInstance.patch(
            `/companies/merchant-of-record-suppliers/${supplierUuid}/payout-details/`,
            payload
        );
    }

    updateOnboardingSupplierPayoutDetails(
        payload: Object,
        onboardingSupplierUuid: string
    ): AxiosPromise<SupplierPayoutDetails> {
        return axiosInstance.put(
            `/companies/mor/onboarding-suppliers/${onboardingSupplierUuid}/payout-details/`,
            payload
        );
    }

    getSupplierCompanyAddresses(
        companyUuid: string
    ): AxiosPromise<CompanyAddress[]> {
        return axiosInstance.get(
            `/companies/merchant-of-record-company-addresses/`,
            {
                params: {
                    company_uuid: companyUuid,
                },
            }
        );
    }

    replaceCompanyAddressAsMoR(
        addressUuid: string,
        payload: any
    ): AxiosPromise<CompanyAddress> {
        return axiosInstance.put(
            `/companies/merchant-of-record-company-addresses/${addressUuid}/`,
            payload
        );
    }

    createCompanyAddressAsMoR(payload: any): AxiosPromise<CompanyAddress> {
        return axiosInstance.post(
            `/companies/merchant-of-record-company-addresses/`,
            payload
        );
    }

    deleteSupplierAddressAsMoR(
        addressUuid: string
    ): AxiosPromise<CompanyAddress> {
        return axiosInstance.delete(
            `/companies/merchant-of-record-company-addresses/${addressUuid}`
        );
    }

    getOnboardingSupplierContacts(
        onboardingSupplierUuid: string
    ): AxiosPromise<SupplierContact[]> {
        return axiosInstance.get(
            `/companies/mor/onboarding-suppliers/${onboardingSupplierUuid}/contacts/`
        );
    }

    createOnboardingSupplierContact(
        payload: Object,
        onboardingSupplierUuid: string
    ): AxiosPromise<SupplierContact> {
        return axiosInstance.post(
            `/companies/mor/onboarding-suppliers/${onboardingSupplierUuid}/contacts/`,
            payload
        );
    }

    updateOnboardingSupplierContact(
        payload: Object,
        onboardingSupplierUuid: string,
        contactUuid: string
    ): AxiosPromise<SupplierContact> {
        return axiosInstance.put(
            `/companies/mor/onboarding-suppliers/${onboardingSupplierUuid}/contacts/${contactUuid}/`,
            payload
        );
    }

    deleteOnboardingSupplierContacts(
        onboardingSupplierUuid: string,
        contactUuid: string
    ): AxiosPromise<SupplierContact[]> {
        return axiosInstance.delete(
            `/companies/mor/onboarding-suppliers/${onboardingSupplierUuid}/contacts/${contactUuid}/`
        );
    }

    updateOnboardingSupplierAddress(
        payload: Object,
        onboardingSupplierUuid: string
    ) {
        return axiosInstance.patch(
            `/companies/mor/onboarding-suppliers/${onboardingSupplierUuid}/address/`,
            payload
        );
    }

    updateOnboardingSupplier(
        payload: {
            duns?: string | null;
            lead_time_days?: number | null;
        },
        onboardingSupplierUuid: string
    ): AxiosPromise<OnboardingSupplier> {
        return axiosInstance.patch(
            `/companies/mor/onboarding-suppliers/${onboardingSupplierUuid}/`,
            payload
        );
    }

    // Supplier Notes APIs

    getSupplierNotes(
        supplierUuid: string
    ): AxiosPromise<MerchantOfRecordNote[]> {
        return axiosInstance.get(
            `/companies/mor/suppliers/${supplierUuid}/notes/`
        );
    }

    updateSupplierNote(
        payload: { body: string },
        supplierUuid: string,
        noteUuid: string
    ): AxiosPromise<MerchantOfRecordNote> {
        return axiosInstance.put(
            `/companies/mor/suppliers/${supplierUuid}/notes/${noteUuid}/`,
            payload
        );
    }

    createSupplierNote(
        payload: { body: string },
        supplierUuid: string
    ): AxiosPromise<MerchantOfRecordNote> {
        return axiosInstance.post(
            `/companies/mor/suppliers/${supplierUuid}/notes/`,
            payload
        );
    }

    deleteSupplierNote(
        supplierUuid: string,
        noteUuid: string
    ): AxiosPromise<void> {
        return axiosInstance.delete(
            `/companies/mor/suppliers/${supplierUuid}/notes/${noteUuid}/`
        );
    }

    // Onboarding Supplier Notes APIs

    getOnboardingSupplierNotes(
        onboardingSupplierUuid: string
    ): AxiosPromise<MerchantOfRecordNote[]> {
        return axiosInstance.get(
            `/companies/mor/onboarding-suppliers/${onboardingSupplierUuid}/notes/`
        );
    }

    createOnboardingSupplierNote(
        payload: { body: string },
        onboardingSupplierUuid: string
    ): AxiosPromise<MerchantOfRecordNote> {
        return axiosInstance.post(
            `/companies/mor/onboarding-suppliers/${onboardingSupplierUuid}/notes/`,
            payload
        );
    }

    updateOnboardingSupplierNote(
        payload: { body: string },
        onboardingSupplierUuid: string,
        noteUuid: string
    ): AxiosPromise<MerchantOfRecordNote> {
        return axiosInstance.put(
            `/companies/mor/onboarding-suppliers/${onboardingSupplierUuid}/notes/${noteUuid}/`,
            payload
        );
    }

    deleteOnboardingSupplierNote(
        supplierUuid: string,
        noteUuid: string
    ): AxiosPromise<void> {
        return axiosInstance.delete(
            `/companies/mor/onboarding-suppliers/${supplierUuid}/notes/${noteUuid}/`
        );
    }

    getSupplierDocumentTypes(): AxiosPromise {
        return axiosInstance.get("companies/supplier-document-types/");
    }

    createSupplierDocument(
        supplierUuid: string,
        payload: FormData
    ): AxiosPromise {
        return axiosInstance.post(
            `companies/suppliers/${supplierUuid}/supplier-documents/`,
            payload,
            { headers: { "Content-Type": "multipart/form-data" } }
        );
    }
}

export default SupplierService;
